import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Calendar, DollarSign, MoreHorizontal, Edit2, Trash2, Check, ExternalLink } from 'lucide-react';
import { Promotion, PromotionStatus } from './types';
interface PromotionCardProps {
  promotion: Promotion;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: PromotionStatus) => void;
  compact?: boolean;
}
const statusColors: Record<PromotionStatus, string> = {
  active: 'bg-green-100 text-green-700 border-green-200',
  applied: 'bg-blue-100 text-blue-700 border-blue-200',
  pending: 'bg-purple-100 text-purple-700 border-purple-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  expired: 'bg-red-100 text-red-700 border-red-200'
};
export function PromotionCard({
  promotion,
  onEdit,
  onDelete,
  onStatusChange,
  compact = false
}: PromotionCardProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const daysUntilDeadline = Math.ceil((new Date(promotion.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  const isUrgent = promotion.status === 'active' && daysUntilDeadline <= 14 && daysUntilDeadline > 0;
  return <motion.div layout initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow relative group ${compact ? 'p-4' : 'p-5'}`} data-id="element-74">
      <div className="flex justify-between items-start mb-3" data-id="element-75">
        <div data-id="element-76">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-77">
            {promotion.bankName}
          </span>
          <h3 className="font-bold text-gray-900 leading-tight mt-1" data-id="element-78">
            {promotion.promotionName}
          </h3>
        </div>
        <div className="relative" ref={menuRef} data-id="element-79">
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-80">
            <MoreHorizontal className="w-5 h-5" data-id="element-81" />
          </button>

          {isMenuOpen && <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-82">
              <button onClick={() => {
            onEdit(promotion.id);
            setIsMenuOpen(false);
          }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-83">
                <Edit2 className="w-4 h-4" data-id="element-84" /> Edit
              </button>
              <button onClick={() => {
            onStatusChange(promotion.id, 'completed');
            setIsMenuOpen(false);
          }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-85">
                <Check className="w-4 h-4" data-id="element-86" /> Mark Completed
              </button>
              <div className="h-px bg-gray-100 my-1" data-id="element-87" />
              <button onClick={() => {
            onDelete(promotion.id);
            setIsMenuOpen(false);
          }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2" data-id="element-88">
                <Trash2 className="w-4 h-4" data-id="element-89" /> Delete
              </button>
            </div>}
        </div>
      </div>

      <div className="flex items-baseline gap-1 mb-4" data-id="element-90">
        <span className="text-2xl font-bold text-gray-900 font-mono" data-id="element-91">
          ${promotion.bonusAmount}
        </span>
        <span className="text-sm text-gray-500" data-id="element-92">bonus</span>
      </div>

      <div className="space-y-2 mb-4" data-id="element-93">
        <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-94">
          <DollarSign className="w-4 h-4 text-gray-400" data-id="element-95" />
          <span data-id="element-96">
            Min. Deposit:{' '}
            <span className="font-mono font-medium" data-id="element-97">
              ${promotion.minAmount.toLocaleString()}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-98">
          <Calendar className={`w-4 h-4 ${isUrgent ? 'text-orange-500' : 'text-gray-400'}`} data-id="element-99" />
          <span className={isUrgent ? 'text-orange-700 font-medium' : ''} data-id="element-100">
            Deadline:{' '}
            <span className="font-mono" data-id="element-101">{formatDate(promotion.deadline)}</span>
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100" data-id="element-102">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[promotion.status]}`} data-id="element-103">
          {promotion.status.charAt(0).toUpperCase() + promotion.status.slice(1)}
        </span>
        <span className="text-xs text-gray-400 font-mono" data-id="element-104">
          {promotion.accountType}
        </span>
      </div>
    </motion.div>;
}